/**
 * Binary File Handler
 * Handles processing of binary file formats (DOCX, PDF, ODT, etc.) through docling service
 */

import DoclingService from '../../parsers/docling-service';
import type { DoclingParseResult } from '../../parsers/docling-service';
import type GatewayService from '../../core/gateway-service';
import { logger } from '../../core/logger';
import { useAppStore } from '../../stores/app-store';
import { toParseOptions } from '../../core/parse-options-mapper';

export interface BinaryFileResult {
  textContent: string;
  rawContent: ArrayBuffer;
  parseResult: DoclingParseResult;
}

export interface BinaryFileHandlerOptions {
  gatewayService?: GatewayService | null;
  useGateway?: boolean;
}

export class BinaryFileHandler {
  private doclingService: DoclingService;

  constructor(options: BinaryFileHandlerOptions = {}) {
    this.doclingService = new DoclingService({
      gatewayService: options.gatewayService || null,
      useGateway: options.useGateway !== undefined ? options.useGateway : true,
    });
  }

  /**
   * A Docling result that was parsed earlier (a stored analysis, #98 part 4)
   * in the shape a fresh parse returns.
   */
  prepareParseResponse(
    ...args: Parameters<DoclingService['prepareParseResponse']>
  ): ReturnType<DoclingService['prepareParseResponse']> {
    return this.doclingService.prepareParseResponse(...args);
  }

  /**
   * Check if docling service supports a given file format
   * @param mimeType - MIME type of the file
   * @param filename - Name of the file
   */
  isFormatSupported(mimeType: string, filename: string): boolean {
    return this.doclingService.isFormatSupported(mimeType, filename);
  }

  /**
   * Process binary file through docling service
   * @param rawContent - File content as ArrayBuffer
   * @param filename - Name of the file
   * @param mimeType - MIME type of the file
   * @returns Processed file data with text content and parse result
   */
  async processBinaryFile(
    rawContent: ArrayBuffer,
    filename: string,
    mimeType: string
  ): Promise<BinaryFileResult> {
    if (!this.doclingService.isFormatSupported(mimeType, filename)) {
      throw new Error(`Unsupported binary format: ${mimeType || filename}`);
    }

    const parseOptions = toParseOptions(useAppStore.getState().parseSettings);

    const parseResult = await this.doclingService.parseDocument(
      rawContent,
      filename,
      mimeType,
      parseOptions
    );

    if (!parseResult.success) {
      throw new Error(
        `Document parsing failed: ${parseResult.error || 'Unknown error'}`
      );
    }

    let textContent = parseResult.text;

    if (!textContent || textContent.trim().length === 0) {
      textContent = '[Document contains no extractable text]';
      logger.warn('Document has no text content:', parseResult.warning);
    }

    return { textContent, rawContent, parseResult };
  }

  /**
   * Process binary file from File object
   * @param file - File object to process
   * @param readFileContentAsBinary - Function to read file content as ArrayBuffer
   * @returns Processed file data
   */
  async processFile(
    file: File,
    readFileContentAsBinary: (file: File) => Promise<ArrayBuffer>
  ): Promise<BinaryFileResult> {
    const rawContent = await readFileContentAsBinary(file);
    return this.processBinaryFile(rawContent, file.name, file.type);
  }
}
